'use client'

import React, { useId, useMemo } from 'react'

import { ExternalLink } from 'lucide-react'
import Link from 'next/link'
import { twMerge } from 'tailwind-merge'

import type { OpportunityType } from '@/types/Opportunity'
import type { SearchOut } from '@/types/Search'

import { normalizeDepartmentName } from '../transforms'
import {
  SimilarOpportunitiesContext,
  useSimilarOpportunitiesContext,
} from './context'
import {
  buildOpportunityHref,
  extractBuyer,
  removeMarkdown,
} from './transforms'

// ============================================================================
// Types
// ============================================================================

interface SimilarOpportunitiesRootProps {
  /** Opportunity type - determines routing for card links */
  type: OpportunityType
  /** Array of similar opportunities to display */
  opportunities?: SearchOut[]
  /** Loading state - shows skeleton when true */
  isLoading?: boolean
  /** Children components */
  children: React.ReactNode
  /** Additional className for the root element */
  className?: string
}

interface SimilarOpportunitiesHeaderProps {
  /** Header text content */
  children: React.ReactNode
  /** Additional className */
  className?: string
}

interface SimilarOpportunitiesGridProps {
  /** Children - should be Card components */
  children: React.ReactNode
  /** Additional className */
  className?: string
}

interface SimilarOpportunitiesCardProps {
  /** The opportunity to display */
  opportunity: SearchOut
  /** Additional className */
  className?: string
}

interface SimilarOpportunitiesEmptyProps {
  /** Empty state message */
  children?: React.ReactNode
  /** Additional className */
  className?: string
}

interface SimilarOpportunitiesSkeletonProps {
  /** Number of skeleton cards to show (default: 6) */
  count?: number
  /** Additional className */
  className?: string
}

// ============================================================================
// Root Component
// ============================================================================

/**
 * Root container for SimilarOpportunitiesV2 compound component
 *
 * Provides context for all child components with opportunity type,
 * data, and loading state.
 */
function SimilarOpportunitiesRoot({
  type,
  opportunities = [],
  isLoading = false,
  children,
  className,
}: SimilarOpportunitiesRootProps) {
  const headingId = useId()

  const contextValue = useMemo(
    () => ({
      type,
      opportunities,
      isLoading,
      headingId: `similar-opportunities-heading-${headingId}`,
    }),
    [type, opportunities, isLoading, headingId],
  )

  // Don't render if no opportunities and not loading
  if (!isLoading && opportunities.length === 0) {
    return null
  }

  return (
    <SimilarOpportunitiesContext.Provider value={contextValue}>
      <section
        aria-labelledby={contextValue.headingId}
        className={twMerge('mt-6', className)}
      >
        {children}
      </section>
    </SimilarOpportunitiesContext.Provider>
  )
}

// ============================================================================
// Header Component
// ============================================================================

/**
 * Semantic header for the similar opportunities section
 *
 * Renders an h2 element with proper aria-labelledby linkage.
 * SEO-optimized: uses h2 in the document hierarchy.
 * De-emphasized styling to not compete with main content.
 * Displays count of opportunities from context.
 */
function SimilarOpportunitiesHeader({
  children,
  className,
}: SimilarOpportunitiesHeaderProps) {
  const { headingId, opportunities } = useSimilarOpportunitiesContext()
  const count = opportunities.length

  return (
    <h2
      id={headingId}
      className={twMerge(
        'mb-3 pl-1 text-sm font-medium text-primary-500 dark:text-primary-400',
        className,
      )}
    >
      {children}
      {count > 0 && <span className="ml-1 tabular-nums">({count})</span>}
    </h2>
  )
}

// ============================================================================
// Grid Component
// ============================================================================

/**
 * Responsive grid container for opportunity cards
 *
 * Layout: 1 column (mobile) → 2 columns (md) → 3 columns (lg)
 */
function SimilarOpportunitiesGrid({
  children,
  className,
}: SimilarOpportunitiesGridProps) {
  return (
    <div
      className={twMerge('grid grid-cols-1 gap-3 md:grid-cols-2', className)}
    >
      {children}
    </div>
  )
}

// ============================================================================
// Card Component
// ============================================================================

/**
 * Card component for displaying a similar opportunity
 *
 * Pure component - renders title, buyer, summary, and active status.
 * SEO-optimized: uses article element with semantic structure.
 * De-emphasized styling to not compete with main content.
 * Uses flex layout with fixed footer for consistent badge alignment.
 */
function SimilarOpportunitiesCard({
  opportunity,
  className,
}: SimilarOpportunitiesCardProps) {
  const { type } = useSimilarOpportunitiesContext()

  const href = buildOpportunityHref(type, opportunity.id)
  const rawBuyer = extractBuyer(opportunity.tags)
  const buyer = rawBuyer
    ? (normalizeDepartmentName(rawBuyer) ?? rawBuyer)
    : null
  const summary = removeMarkdown(opportunity.ai_summary)

  return (
    <article
      className={twMerge(
        'group flex h-full flex-col rounded-lg bg-primary-50/70 px-2 py-3 transition-colors hover:bg-primary-100/80 dark:bg-primary-800/40 dark:hover:bg-primary-800/60',
        className,
      )}
    >
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={opportunity.title}
        className="flex flex-1 flex-col"
      >
        {/* Content Section - flex column to fill available space */}
        <div className="flex flex-1 flex-col gap-1.5">
          {/* Title row - badge + title + external link icon */}
          <div className="flex items-start gap-1.5">
            <h3 className="line-clamp-2 flex-1 text-pretty text-sm font-medium leading-tight text-primary-700 group-hover:text-blue-600 dark:text-primary-300 dark:group-hover:text-blue-400">
              <span
                className={twMerge(
                  'mr-1.5 inline-flex translate-y-[-1px] items-center rounded-full px-1.5 py-0.5 text-xs font-medium',
                  opportunity.active
                    ? 'bg-green-100/80 text-green-600 dark:bg-green-900/20 dark:text-green-500'
                    : 'bg-primary-100/80 text-primary-400 dark:bg-primary-700/50 dark:text-primary-500',
                )}
              >
                {opportunity.active ? 'Active' : 'Closed'}
              </span>
              {opportunity.title}
            </h3>
            <ExternalLink className="h-4 w-4 shrink-0 text-primary-400 opacity-0 transition-opacity group-hover:opacity-100 dark:text-primary-500" />
          </div>

          {/* Buyer / Agency - subtitle below title */}
          {buyer && (
            <span className="block truncate text-sm text-primary-400 dark:text-primary-500">
              {buyer}
            </span>
          )}

          {/* Summary - fills remaining vertical space, max 4 lines */}
          {summary && (
            <p className="line-clamp-4 min-h-0 flex-1 text-pretty text-sm leading-relaxed text-primary-500 group-hover:text-primary-600 dark:text-primary-400 dark:group-hover:text-primary-300">
              {summary}
            </p>
          )}
        </div>
      </Link>
    </article>
  )
}

// ============================================================================
// Empty Component
// ============================================================================

/**
 * Empty state component shown when no similar opportunities are found
 *
 * Note: The Root component already handles hiding when empty,
 * so this is primarily for explicit empty state handling if needed.
 */
function SimilarOpportunitiesEmpty({
  children = 'No related opportunities found.',
  className,
}: SimilarOpportunitiesEmptyProps) {
  const { opportunities, isLoading } = useSimilarOpportunitiesContext()

  // Don't show empty state while loading
  if (isLoading || opportunities.length > 0) {
    return null
  }

  return (
    <div
      className={twMerge(
        'rounded-lg border border-primary-200 bg-primary-50 p-6 text-center text-sm text-primary-500 dark:border-primary-700 dark:bg-primary-800/50 dark:text-primary-400',
        className,
      )}
    >
      {children}
    </div>
  )
}

// ============================================================================
// Skeleton Component
// ============================================================================

/**
 * Skeleton card for loading state
 * Matches Card component structure with badge left of title
 */
function SkeletonCard() {
  return (
    <div className="flex h-full flex-col rounded-lg bg-primary-50/70 p-3 dark:bg-primary-800/40">
      {/* Content section - flex column */}
      <div className="flex flex-1 flex-col gap-1.5">
        {/* Title with inline badge */}
        <div className="flex items-start gap-1.5">
          <div className="h-4 w-10 shrink-0 animate-pulse rounded-full bg-primary-200/60 dark:bg-primary-700/40" />
          <div className="h-4 w-4/5 animate-pulse rounded bg-primary-200/80 dark:bg-primary-700/60" />
        </div>
        {/* Buyer skeleton - subtitle */}
        <div className="h-3 w-1/3 animate-pulse rounded bg-primary-200/60 dark:bg-primary-700/40" />
        {/* Summary skeleton */}
        <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-hidden">
          <div className="h-3 w-full animate-pulse rounded bg-primary-200/60 dark:bg-primary-700/40" />
          <div className="h-3 w-full animate-pulse rounded bg-primary-200/60 dark:bg-primary-700/40" />
          <div className="h-3 w-4/5 animate-pulse rounded bg-primary-200/60 dark:bg-primary-700/40" />
        </div>
      </div>
    </div>
  )
}

/**
 * Loading skeleton for similar opportunities grid
 *
 * Shows placeholder cards while data is loading.
 * Default: 6 cards (fills 2 rows on large screens)
 */
function SimilarOpportunitiesSkeleton({
  count = 4,
  className,
}: SimilarOpportunitiesSkeletonProps) {
  return (
    <div
      className={twMerge('grid grid-cols-1 gap-3 md:grid-cols-2', className)}
    >
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  )
}

// ============================================================================
// Compound Component Export
// ============================================================================

/**
 * SimilarOpportunitiesV2 - Compositional component for displaying similar opportunities
 *
 * SEO-optimized compound component with semantic HTML structure.
 * Type-agnostic design works with all opportunity types.
 *
 * @example
 * ```tsx
 * <SimilarOpportunitiesV2
 *   type="federal_contract"
 *   opportunities={data}
 *   isLoading={isLoading}
 * >
 *   <SimilarOpportunitiesV2.Header>
 *     Related Contract Opportunities
 *   </SimilarOpportunitiesV2.Header>
 *   {isLoading ? (
 *     <SimilarOpportunitiesV2.Skeleton />
 *   ) : (
 *     <SimilarOpportunitiesV2.Grid>
 *       {opportunities.map(opp => (
 *         <SimilarOpportunitiesV2.Card key={opp.id} opportunity={opp} />
 *       ))}
 *     </SimilarOpportunitiesV2.Grid>
 *   )}
 * </SimilarOpportunitiesV2>
 * ```
 */
const SimilarOpportunitiesV2 =
  SimilarOpportunitiesRoot as typeof SimilarOpportunitiesRoot & {
    Header: typeof SimilarOpportunitiesHeader
    Grid: typeof SimilarOpportunitiesGrid
    Card: typeof SimilarOpportunitiesCard
    Empty: typeof SimilarOpportunitiesEmpty
    Skeleton: typeof SimilarOpportunitiesSkeleton
  }

SimilarOpportunitiesV2.Header = SimilarOpportunitiesHeader
SimilarOpportunitiesV2.Grid = SimilarOpportunitiesGrid
SimilarOpportunitiesV2.Card = SimilarOpportunitiesCard
SimilarOpportunitiesV2.Empty = SimilarOpportunitiesEmpty
SimilarOpportunitiesV2.Skeleton = SimilarOpportunitiesSkeleton

export { SimilarOpportunitiesV2 }
export type {
  SimilarOpportunitiesRootProps,
  SimilarOpportunitiesHeaderProps,
  SimilarOpportunitiesGridProps,
  SimilarOpportunitiesCardProps,
  SimilarOpportunitiesEmptyProps,
  SimilarOpportunitiesSkeletonProps,
}
